import { useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { useQuery } from '@apollo/client'
import { Box } from '@factorialco/deprecated-design-system'
import { AvailableModules, Features } from 'generated/featureDefinitions'
import {
  AnalyticsPageDocument,
  GetFeatureAvailabilitiesDocument,
} from 'generated/resources/graphql'
import { keyBy } from 'lodash'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'
import { isFeatureAvailabilityEnabled } from 'modules/core/lib/isFeatureAvailabilityEnabled'
import { isModuleAvailabilityEnabled } from 'modules/core/lib/isModuleAvailabilityEnabled'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import Chart from 'modules/performance_analytics/components/Chart'
import GroupByDropdown, {
  GroupByOption,
} from 'modules/performance_analytics/components/GroupByDropdown'
import { GroupByProvider } from 'modules/performance_analytics/components/GroupByDropdown/GroupByProvider'
import PerformanceInsightsSummaryDatatable from 'modules/performance_analytics/components/PerformanceInsightsSummaryDatatable'
import { ChartProvider, ChartType } from 'modules/performance_analytics/lib/ChartContext'
import nineBoxGridBackground from 'modules/performance_analytics/lib/assets/blankSlate/locked-nine-box-grid-bg.svg'
import performanceBackground from 'modules/performance_analytics/lib/assets/blankSlate/locked-performance-bg.svg'
import { ValidReasons } from 'modules/upsell/hooks/useCreateUpsellRequest'
import { UpsellingKit } from 'modules/upselling_kit/components'

interface AnalitycsPageProps {
  developmentMode?: boolean
}

type DashboardFilter = ChartType | 'headcount'

const performanceCharts = ['line_chart', '9_box_grid']

const AnalitycsPage = ({ developmentMode = false }: AnalitycsPageProps) => {
  const { company } = useCurrentSession()
  const { pathname } = useLocation()

  const [activeFilter, setActiveFilter] = useState<DashboardFilter>('line_chart')

  const { loading, data, error } = useQuery(AnalyticsPageDocument, {
    variables: {
      companyId: company.id,
      moduleAvailabilityIds: [AvailableModules.review_process],
    },
  })
  const { loading: loadingFeaturesData, data: featuresData } = useQuery(
    GetFeatureAvailabilitiesDocument,
    {
      fetchPolicy: 'cache-first',
      variables: {
        modules: [
          AvailableModules.review_process,
          AvailableModules.projects,
          AvailableModules.engagement,
        ],
        companyId: company.id,
      },
    }
  )
  const featureAvailabilities = featuresData?.companies.featureAvailabilities ?? []

  const features = keyBy(data?.features?.featuresConnection?.nodes, 'name')
  const engagementSignalsEnabled = features['dev_performance_engagement_signals']?.enabled ?? false
  const aggregationsEnabled = features['dev_performance_aggregations']?.enabled ?? false
  const employeePulseQuestionEnabled =
    features['engagement_employee_pulse_questions']?.enabled ||
    (featureAvailabilities &&
      isFeatureAvailabilityEnabled(
        featureAvailabilities,
        Features.engagement_employee_pulse_questions
      )) ||
    false
  const teams = data?.teams.teamsConnection.edges.map(({ node }) => node) || []
  const canCreateReviewProcesses =
    data?.performance._policies.createReviewProcessContextless.can ?? false
  const hasReviewProcesses = !!data?.performance.reviewFinalEmployeeScoresConnection.totalCount
  const genders = data?.companies.gendersConnection.nodes || []
  const locations = data?.locations.locationsConnection.nodes || []
  const legalEntities = data?.companies.legalEntitiesConnection.nodes || []
  const nationalities =
    data?.reports.insightsFiltersConnection.nodes.find(
      (filter) => filter.filterType === 'nationalities'
    )?.values || []
  const currentChart = useMemo(() => {
    if (pathname.includes('headcount')) {
      return 'headcount'
    }

    if (pathname.includes('nine_box_grid')) {
      return '9_box_grid'
    }

    if (pathname.includes('pulse_survey')) {
      return 'employee_pulse'
    }

    return 'line_chart'
  }, [pathname])

  const moduleAvailabilities = data?.companies?.moduleAvailabilitiesConnection?.nodes ?? []

  const canRequestUpsell = data?.upsell?._policies?.canRequestUpsell?.can ?? false

  const hasPerformance =
    moduleAvailabilities &&
    isModuleAvailabilityEnabled(moduleAvailabilities, AvailableModules.review_process)

  const hasProjects =
    moduleAvailabilities &&
    isModuleAvailabilityEnabled(moduleAvailabilities, AvailableModules.projects)

  const hasCustomScoreConfigurationAvailable =
    featureAvailabilities &&
    isFeatureAvailabilityEnabled(
      featureAvailabilities,
      Features.review_process_custom_score_configuration
    )
  const showPerformanceEnterpriseUpsellingKit = !hasCustomScoreConfigurationAvailable

  const isCurrentPagePerformance = performanceCharts.includes(currentChart)
  const showPerformanceUpsell = canRequestUpsell && !hasPerformance && isCurrentPagePerformance

  const groupByOptions: GroupByOption[] = useMemo(() => {
    const options: GroupByOption[] = [
      {
        label: i18n.t('performance_process.analytics.datatable.group_by.gender'),
        value: 'gender',
      },
      {
        label: i18n.t('performance_process.analytics.datatable.group_by.legal_entity'),
        value: 'legal_entity',
      },
      { label: i18n.t('performance_process.analytics.datatable.group_by.team'), value: 'team' },
      {
        label: i18n.t('performance_process.analytics.datatable.group_by.nationality'),
        value: 'nationality',
      },
      {
        label: i18n.t('performance_process.analytics.datatable.group_by.manager'),
        value: 'manager',
      },
      {
        label: i18n.t('performance_process.analytics.datatable.group_by.workspace'),
        value: 'workspace',
      },
    ]

    return options
  }, [])

  useEffect(() => {
    if (currentChart === 'headcount' || (!hasPerformance && isCurrentPagePerformance)) {
      setActiveFilter('headcount')
    } else {
      setActiveFilter(currentChart)
    }
  }, [currentChart, hasPerformance, isCurrentPagePerformance])

  const upsellTitle =
    currentChart === '9_box_grid'
      ? i18n.t('performance_process.analytics.upsell.9_box_grid.title')
      : i18n.t('performance_process.analytics.upsell.performance.title')

  const upsellDescription =
    currentChart === '9_box_grid'
      ? i18n.t('performance_process.analytics.upsell.9_box_grid.description')
      : i18n.t('performance_process.analytics.upsell.performance.description')

  const upsellBackground =
    currentChart === '9_box_grid' ? nineBoxGridBackground : performanceBackground

  return (
    <ResponsivePage.Body loading={loading || loadingFeaturesData} showHeader={false} error={error}>
      {showPerformanceUpsell ? (
        <UpsellingKit.EmptyState
          moduleRequested={AvailableModules.review_process}
          requestSource="Talent dashboard"
          requestReason={ValidReasons.MissingAddon}
          background={upsellBackground}
          title={upsellTitle}
          description={upsellDescription}
        />
      ) : (
        <Box gap="s24">
          <GroupByProvider>
            <ChartProvider>
              <Box gap="s12" flexDirection="column">
                {activeFilter !== '9_box_grid' && (
                  <Box style={{ width: 'fit-content' }}>
                    <GroupByDropdown options={groupByOptions} />
                  </Box>
                )}

                <PerformanceInsightsSummaryDatatable
                  teams={teams}
                  hasReviewProcesses={hasReviewProcesses}
                  canCreateReviewProcesses={canCreateReviewProcesses}
                  engagementSignalsEnabled={developmentMode || engagementSignalsEnabled}
                  aggregationsEnabled={developmentMode || aggregationsEnabled}
                  employeePulseQuestionEnabled={developmentMode || employeePulseQuestionEnabled}
                  developmentMode={developmentMode}
                  genders={genders}
                  locations={locations}
                  legalEntities={legalEntities}
                  nationalities={nationalities}
                  activeFilter={activeFilter}
                  performanceEnabled={hasPerformance}
                  projectsEnabled={hasProjects}
                >
                  <Chart
                    activeFilter={activeFilter}
                    performanceEnabled={hasPerformance}
                    showPerformanceEnterpriseUpsellingKit={showPerformanceEnterpriseUpsellingKit}
                  />
                </PerformanceInsightsSummaryDatatable>
              </Box>
            </ChartProvider>
          </GroupByProvider>
        </Box>
      )}
    </ResponsivePage.Body>
  )
}

export default AnalitycsPage
